import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { ROUTE_PERMISSIONS } from "../../routes/routePermissions";
import { menuConfig } from "../../config/menuConfig";
import { canAccessByRule, resolveMenuItemRule } from "../../utils/menuPermissions";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 10vh;
  z-index: 9999;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 560px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideDown 0.15s ease-out;

  @keyframes slideDown {
    from {
      transform: translateY(-8px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  gap: 10px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-family: var(--font-body);
  font-size: 14px;
  color: #0f172a;
  background: transparent;

  &::placeholder {
    color: #94a3b8;
  }
`;

const Kbd = styled.kbd`
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-family: var(--font-mono);
  color: #64748b;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
`;

const ResultsList = styled.div`
  max-height: 330px;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${(props) => (props.$active ? "#f1f5f9" : "transparent")};
  transition: background-color 0.1s ease;

  &:hover {
    background-color: #f1f5f9;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemLabel = styled.span`
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
`;

const ItemBreadcrumb = styled.span`
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
`;

const SelectHint = styled.span`
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
  font-family: var(--font-body);
`;

const DialogFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-top: 1px solid #f1f5f9;
  background-color: #f8fafc;
  font-size: 11px;
  color: #64748b;
  font-family: var(--font-body);
`;

const ShortcutGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Shortcut = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export default function CommandPalette({ isOpen, onClose, onAction }) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Indexar recursivamente los módulos y subopciones navegables a los que el usuario tiene acceso
  const searchableItems = useMemo(() => {
    const items = [];

    const traverse = (node, parentLabels = [], parentRule = null) => {
      const currentRule = resolveMenuItemRule({
        item: node,
        parentRule,
        routePermissions: ROUTE_PERMISSIONS,
      });

      if (!canAccessByRule({ rule: currentRule, hasPermission })) {
        return;
      }

      const hasChildren = node.children && node.children.length > 0;
      const isNavigableRoute = typeof node.route === "string" && node.route.startsWith("/");
      const hasAction = Boolean(node.action);

      // Si es un nodo hoja con ruta o acción, es un destino navegable
      if (!hasChildren && (isNavigableRoute || hasAction)) {
        items.push({
          id: node.id,
          label: node.label,
          route: node.route,
          action: node.action,
          breadcrumb: [...parentLabels, node.label].join(" › "),
        });
      }

      // Si tiene subopciones, continuar recorriendo
      if (hasChildren) {
        node.children.forEach((child) => {
          traverse(child, [...parentLabels, node.label], currentRule);
        });
      }
    };

    menuConfig.forEach((rootMenu) => {
      traverse(rootMenu, []);
    });

    return items;
  }, [hasPermission]);

  // Filtrar según el texto de búsqueda
  const filteredItems = useMemo(() => {
    if (!query.trim()) return searchableItems;
    const cleanQuery = query.toLowerCase().trim();
    return searchableItems.filter((item) =>
      item.label.toLowerCase().includes(cleanQuery) ||
      item.breadcrumb.toLowerCase().includes(cleanQuery)
    );
  }, [searchableItems, query]);

  // Enfocar input al abrir
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Ajustar el índice seleccionado cuando cambian los resultados
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Asegurar que el elemento seleccionado esté visible en el scroll
  useEffect(() => {
    if (listRef.current && listRef.current.children[selectedIndex]) {
      listRef.current.children[selectedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  // Manejar selección de un elemento
  const handleSelect = (item) => {
    if (!item) return;
    onClose();
    if (item.action) {
      onAction?.(item.action);
    } else if (item.route && item.route.startsWith("/")) {
      navigate(item.route);
    }
  };

  // Manejar teclas dentro del modal
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredItems.length || 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev <= 0 ? (filteredItems.length - 1 || 0) : prev - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredItems[selectedIndex]) {
        handleSelect(filteredItems[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <SearchHeader>
          <SearchInput
            ref={inputRef}
            placeholder="Buscar módulo, opción o reporte..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Kbd>ESC</Kbd>
        </SearchHeader>

        <ResultsList ref={listRef}>
          {filteredItems.length === 0 ? (
            <EmptyState>No se encontraron opciones para "{query}"</EmptyState>
          ) : (
            filteredItems.map((item, index) => (
              <ResultItem
                key={item.id}
                $active={index === selectedIndex}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => handleSelect(item)}
              >
                <ItemInfo>
                  <ItemLabel>{item.label}</ItemLabel>
                  <ItemBreadcrumb>{item.breadcrumb}</ItemBreadcrumb>
                </ItemInfo>
                <SelectHint>↵ Abrir</SelectHint>
              </ResultItem>
            ))
          )}
        </ResultsList>

        <DialogFooter>
          <ShortcutGroup>
            <Shortcut>
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd> Navegar
            </Shortcut>
            <Shortcut>
              <Kbd>↵</Kbd> Seleccionar
            </Shortcut>
          </ShortcutGroup>
          <Shortcut>
            <Kbd>ESC</Kbd> Cerrar
          </Shortcut>
        </DialogFooter>
      </Dialog>
    </Overlay>
  );
}

